import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { Sprout, ImagePlus, UserCircle, MapPin, CheckCircle2, ChevronRight } from "lucide-react";

export function ProfileCompletionBanner() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  if (!user) return null;

  const needsCover = !String((user as any).coverImageUrl || "").trim();
  const needsAvatar = !String((user as any).profileImageUrl || "").trim();
  const hasAddressText =
    Boolean(String((user as any).addressLine1 || "").trim()) &&
    Boolean(String((user as any).zipCode || "").trim());
  const lat = (user as any).latitude == null ? 0 : Number((user as any).latitude);
  const lng = (user as any).longitude == null ? 0 : Number((user as any).longitude);
  const isGeocoded = Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
  const needsAddress = !hasAddressText;
  // Address is typed but didn't resolve to a map point. The server blocks
  // publishing on this (missing geocode), so surface it as a fixable task
  // instead of letting the listing fail silently at publish time.
  const needsGeocode = hasAddressText && !isGeocoded;

  if (!needsCover && !needsAvatar && !needsAddress && !needsGeocode) return null;

  const tasks = [
    {
      done: !needsCover,
      icon: ImagePlus,
      title: "Add a cover photo",
      hint: "A nice wide shot of your farm sets the scene for your profile.",
    },
    {
      done: !needsAvatar,
      icon: UserCircle,
      title: "Add a profile photo",
      hint: "Anything works — a plant, your produce, a chicken, or your logo.",
    },
    {
      done: !needsAddress && !needsGeocode,
      icon: MapPin,
      title: needsGeocode ? "Confirm your address on the map" : "Add your farm address",
      hint: needsGeocode
        ? "We couldn't pin your address on the map. Re-enter it so your listings can publish."
        : "Required to post products — listings won't publish without it.",
    },
  ];

  return (
    <div className="container mx-auto px-4 pt-3">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-brand-secondary/10 p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/15">
            <Sprout className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold text-foreground">
              Finish setting up your farm profile
            </h3>
            <p className="mt-1 text-sm leading-snug text-muted-foreground">
              Tap below to open Edit Profile, then add a photo or address. It only
              takes a minute and helps buyers find and trust your farm.
            </p>
            <div className="mt-3 space-y-2">
              {tasks.map((task) => (
                <div key={task.title} className="flex items-start gap-2.5">
                  {task.done ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-secondary" />
                  ) : (
                    <task.icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                  )}
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-semibold ${
                        task.done ? "text-muted-foreground line-through" : "text-foreground"
                      }`}
                    >
                      {task.title}
                    </p>
                    {!task.done && (
                      <p className="text-xs leading-snug text-muted-foreground">{task.hint}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button
              type="button"
              onClick={() => setLocation("/account?edit=1")}
              className="mt-4"
              data-testid="button-complete-profile"
            >
              Add photos &amp; address
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
